import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import apiService, { WatchlistResponse, WatchlistMovieAdd } from '../services/api';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Dialog = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.75rem;
  width: 90%;
  max-width: 420px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
`;

const DialogTitle = styled.h2`
  margin: 0 0 0.25rem 0;
  color: #333;
  font-size: 1.2rem;
`;

const DialogSubtitle = styled.p`
  margin: 0 0 1.25rem 0;
  color: #666;
  font-size: 0.9rem;
`;

const ListScroll = styled.div`
  max-height: 220px;
  overflow-y: auto;
  margin-bottom: 1rem;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`;

const ListOption = styled.button<{ $selected: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0.75rem 1rem;
  border: none;
  background: ${p => p.$selected ? '#f0f0ff' : 'white'};
  color: ${p => p.$selected ? '#667eea' : '#333'};
  font-weight: ${p => p.$selected ? '600' : '400'};
  cursor: pointer;
  text-align: left;
  border-bottom: 1px solid #f3f4f6;

  &:last-child { border-bottom: none; }
  &:hover { background: #f8f8ff; }
`;

const MovieCount = styled.span`
  font-size: 0.78rem;
  color: #aaa;
  font-weight: 400;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin: 1rem 0;
  color: #aaa;
  font-size: 0.82rem;

  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
`;

const NewListRow = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const Input = styled.input`
  flex: 1;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.9rem;

  &:focus { outline: none; border-color: #667eea; }
`;

const Buttons = styled.div`
  display: flex;
  gap: 0.75rem;
  justify-content: flex-end;
  margin-top: 1.25rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.5rem 1.25rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;

  ${p => p.$variant === 'primary' ? `
    background: #667eea;
    color: white;
    &:hover { background: #5a67d8; }
  ` : `
    background: #f3f4f6;
    color: #333;
    border: 1px solid #e5e7eb;
    &:hover { background: #e9ecef; }
  `}

  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

const ErrorMsg = styled.div`
  color: #c53030;
  font-size: 0.85rem;
  margin-top: 0.5rem;
`;

interface Props {
  movie: WatchlistMovieAdd;
  onClose: () => void;
  onAdded: (watchlistId: string, watchlistName: string) => void;
}

const AddToWatchlistModal: React.FC<Props> = ({ movie, onClose, onAdded }) => {
  const [watchlists, setWatchlists] = useState<WatchlistResponse[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiService.getWatchlists().then(setWatchlists).catch(() => {});
  }, []);

  const handleAdd = async () => {
    setError('');
    setLoading(true);
    try {
      let targetId = selectedId;
      let targetName = '';

      if (!targetId) {
        const name = newName.trim();
        if (!name) { setError('Enter a list name or select an existing list.'); setLoading(false); return; }
        const created = await apiService.createWatchlist(name);
        targetId = created.id;
        targetName = created.name;
      } else {
        targetName = watchlists.find(w => w.id === targetId)?.name ?? '';
      }

      await apiService.addMovieToWatchlist(targetId, movie);
      onAdded(targetId, targetName);
    } catch (e: any) {
      setError(e.response?.data?.detail || 'Failed to add to watchlist');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Overlay onClick={() => !loading && onClose()}>
      <Dialog onClick={e => e.stopPropagation()}>
        <DialogTitle>Add to Watchlist</DialogTitle>
        <DialogSubtitle>{movie.title}</DialogSubtitle>

        {watchlists.length > 0 && (
          <ListScroll>
            {watchlists.map(w => (
              <ListOption
                key={w.id}
                $selected={selectedId === w.id}
                onClick={() => { setSelectedId(w.id); setNewName(''); }}
              >
                {w.name}
                <MovieCount>{w.movie_count} movie{w.movie_count !== 1 ? 's' : ''}</MovieCount>
              </ListOption>
            ))}
          </ListScroll>
        )}

        <Divider>{watchlists.length > 0 ? 'or create new' : 'Create a watchlist'}</Divider>

        <NewListRow>
          <Input
            placeholder="New list name…"
            value={newName}
            onChange={e => { setNewName(e.target.value); setSelectedId(null); }}
            onKeyDown={e => e.key === 'Enter' && handleAdd()}
          />
        </NewListRow>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Buttons>
          <Button onClick={onClose} disabled={loading}>Cancel</Button>
          <Button $variant="primary" onClick={handleAdd} disabled={loading || (!selectedId && !newName.trim())}>
            {loading ? 'Adding…' : 'Add'}
          </Button>
        </Buttons>
      </Dialog>
    </Overlay>
  );
};

export default AddToWatchlistModal;
